import {
    AGGREGATED_EMAIL_TEMPLATES_FILTER_OPTIONS,
    TEMPLATE_SOURCE,
    TEMPLATE_TYPE,
} from '@/components/broadcast/constants/emailBroadcast.constants';

import { getBardTemplates, getAllUnlayerEmailFunnelSavedTemplates } from '@/api/campaigns/campaignUnlayerEmail';

const parseAggregatedEmailTemplatesPageToken = (token) => {
    const decodedToken = Buffer.from(token, 'base64').toString('utf8');
    const json = JSON.parse(decodedToken);

    let templateSource;

    if (json.templateSource === TEMPLATE_SOURCE.UNLAYER) {
        templateSource = TEMPLATE_SOURCE.UNLAYER;
    } else {
        templateSource = TEMPLATE_SOURCE.BARD;
    }

    return {
        templateSource,
        rawPageToken: json.rawPageToken,
    };
};

const getAggregatedEmailTemplatesPageToken = (token, templateSource) => {
    const aggregatedToken = {
        templateSource,
        rawPageToken: token,
    };

    const encodedToken = Buffer.from(JSON.stringify(aggregatedToken)).toString('base64');

    return encodedToken;
};

const fetchUnlayerTemplates = async(rawPageToken, search) => {
    const templates = [];

    const unlayerPage = !rawPageToken ? 0 : Number.parseInt(rawPageToken, 10);

    const data = await getAllUnlayerEmailFunnelSavedTemplates(unlayerPage, search);

    const { data: { templates: unlayerTemplates, nextPage } } = data;

    unlayerTemplates.forEach((t) => {
        templates.push({
            id: t.id,
            name: t.title,
            title: t.title,
            html: t.html,
            htmlPreviewUrl: t.htmlPreviewUrl,
            templateType: TEMPLATE_TYPE.UNLAYER,
        });
    });

    const nextPageToken = nextPage && `${unlayerPage + 1}`;

    return {
        nextPageToken,
        templates,
    };
};

const fetchBardTemplates = async(rawPageToken, search) => {
    const PAGE_SIZE = 25;
    const templates = [];

    const data = await getBardTemplates(rawPageToken, PAGE_SIZE, search);

    const { data: { nextPageToken, bardTemplates } } = data;

    bardTemplates.forEach((t) => {
        templates.push({
            id: t.id,
            name: t.title,
            html: t.htmlBody,
            design: t.bardJson,
            templateType: t.status === 'PRIVATE' ? TEMPLATE_TYPE.BARD_PRIVATE : TEMPLATE_TYPE.BARD_SENT,
        });
    });

    return {
        nextPageToken,
        templates,
    };
};

export const aggregatedEmailTemplates = async (inputPageToken, params) => {
    const { search, filter } = params;

    /*  section #1.
        Determine which template source to fetch templates from
        And the page token to use when fetching from that template source
    */
    let templateSource;

    // [info] `rawPageToken` is named as such because it *may* need to be parsed further according to the needs of the data source.
    // As of this commit, Unlayer source requires page token to be a number but bard accepts a string
    // It is left upto the template fetching util functions to parse it (or use it directly) according to their needs.
    let rawPageToken;

    if (inputPageToken === null) { // checks if the user has requested data from the first page
        rawPageToken = null;

        if (filter === AGGREGATED_EMAIL_TEMPLATES_FILTER_OPTIONS.UNLAYER) {
            templateSource = TEMPLATE_SOURCE.UNLAYER;
        } else {
            templateSource = TEMPLATE_SOURCE.BARD;
        }
    } else {
        // User has requested data from a specific page.
        // Extract the details from the input page token
        ({ templateSource, rawPageToken } = parseAggregatedEmailTemplatesPageToken(inputPageToken));
    }
    // end section #1

    /*  section #2
        Fetch templates and rawNextPageToken, using templateSource and rawPageToken obtained from section #1.
        [info] `rawNextPageToken` is named as such because this is the page token obtained from either data source
        we cannot send it as is to the user because we also need to send which template source this belongs to.
    */
    let rawNextPageToken;
    let templates;

    if (templateSource === TEMPLATE_SOURCE.UNLAYER) {
        ({ nextPageToken: rawNextPageToken, templates } = await fetchUnlayerTemplates(rawPageToken, search));
    } else {
        ({ nextPageToken: rawNextPageToken, templates } = await fetchBardTemplates(rawPageToken, search));
    }
    // end section 2

    // section #3
    // Generate nextPageToken
    let nextPageToken;

    if (!rawNextPageToken) { // end of results check.
        if (filter === AGGREGATED_EMAIL_TEMPLATES_FILTER_OPTIONS.ALL && templateSource === TEMPLATE_SOURCE.BARD) {
            // If we reached the end of bard templates and the user has requested all templates
            // then we need to point them to the start of unlayer templates.
            nextPageToken = getAggregatedEmailTemplatesPageToken(null, TEMPLATE_SOURCE.UNLAYER);

            if (templates.length === 0) {
                return aggregatedEmailTemplates(nextPageToken, params);
            }
        } else {
            // let the user know there are no more results
            nextPageToken = null;
        }
    } else {
        // there are more results to fetch from the template source we were fetching from
        // then point them to the next page.
        nextPageToken = getAggregatedEmailTemplatesPageToken(rawNextPageToken, templateSource);
    }
    // end section #3

    return {
        templates,
        nextPageToken,
    };
};
